import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import type { TiaText } from '@realplc/types';
import { useTranslation } from '../../i18n';
import { loadTiaAgentRelease, requestOpenTiaAgentSettings, resolveTiaAgentDownloadUrl } from '../../services/tiaAgentRelease';

/** What upgrading the Agent adds, and the way to it (E1, T11). */
export const TIA_AGENT_UPGRADE_TEXT: { adds: TiaText; download: TiaText; openSettings: TiaText } = {
  adds: {
    zh: '升级 Agent 后，TIA 里没开工程也能自动新建工程来验证，还能在输入框上方选任意工程。',
    en: 'An upgraded Agent creates a project by itself when none is open in TIA, and lets you choose any project above the input box.',
  },
  download: { zh: '下载新版 Agent', en: 'Download the new Agent' },
  openSettings: { zh: '升级 Agent', en: 'Upgrade the Agent' },
};

/**
 * The way out where an Agent from before P2 stands in the way
 * (AGENT_UPGRADE_REQUIRED): what upgrading adds, and the installer the Agent
 * settings dialog links to, the latest release's (loadTiaAgentRelease). Until
 * that link is known, or when there is none, the button opens that dialog.
 */
export const TiaAgentUpgradeEntry: React.FC = () => {
  const { language } = useTranslation();
  const say = (text: TiaText) => (language === 'Chinese' ? text.zh : text.en);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTiaAgentRelease()
      .then((release) => {
        if (!cancelled) setDownloadUrl(resolveTiaAgentDownloadUrl(release));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const action = 'inline-flex items-center gap-1 rounded-md border border-black/15 bg-white/70 px-2 py-1 font-semibold transition-colors hover:bg-white dark:border-white/20 dark:bg-black/20 dark:hover:bg-black/40';
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="leading-relaxed">{say(TIA_AGENT_UPGRADE_TEXT.adds)}</span>
      {downloadUrl ? (
        <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className={action}>
          <Download className="h-3 w-3" />
          {say(TIA_AGENT_UPGRADE_TEXT.download)}
        </a>
      ) : (
        <button type="button" onClick={requestOpenTiaAgentSettings} className={action}>
          <Download className="h-3 w-3" />
          {say(TIA_AGENT_UPGRADE_TEXT.openSettings)}
        </button>
      )}
    </div>
  );
};
